import React, { useState } from 'react';
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
  IconButton,
  InputBase,
  Badge,
  Avatar,
  Popper,
  ClickAwayListener,
  Divider,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useBreadcrumb } from '../../../shared/hooks/useBreadcrumb';

const colors = {
  surface: '#f8f9ff',
  surfaceContainerLowest: '#ffffff',
  onSurface: '#0b1c30',
  onSurfaceVariant: '#434656',
  outline: '#737688',
  outlineVariant: '#c3c5d9',
  primary: '#003ec7',
  onPrimary: '#ffffff',
  error: '#ba1a1a',
};

export interface HeaderProps {
  name: string;
  email: string;
  onLogout: () => void;
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

export function Header({ name, email, onLogout }: HeaderProps) {
  const location = useLocation();
  const { labels } = useBreadcrumb();
  const pathnames = location.pathname.split('/').filter((x) => x);
  const [avatarAnchor, setAvatarAnchor] = useState<HTMLElement | null>(null);

  const getLabel = (path: string) => {
    if (labels[path]) return labels[path];
    return path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' ');
  };

  const handleAvatarClick = (e: React.MouseEvent<HTMLElement>) => {
    setAvatarAnchor((prev) => (prev ? null : e.currentTarget));
  };

  const handleLogout = () => {
    setAvatarAnchor(null);
    onLogout();
  };

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' },
        flexShrink: 0,
        width: '100%',
      }}
    >
      {/* Top bar with border */}
      <Box
        sx={{
          width: '100%',
          borderBottom: `1px solid ${colors.outlineVariant}`,
          backgroundColor: colors.surface,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 4,
            gap: 2,
          }}
        >
          {/* Search */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: '#eff4ff',
              border: `1px solid ${colors.outlineVariant}`,
              borderRadius: '8px',
              px: 2,
              py: 0.75,
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:focus-within': {
                borderColor: colors.primary,
                boxShadow: '0 0 0 3px rgba(0, 76, 237, 0.15)',
              },
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: colors.outline }} />
            <InputBase
              placeholder="Search..."
              sx={{
                fontSize: '13px',
                fontFamily: 'Inter, sans-serif',
                color: colors.onSurface,
                width: 180,
                '& input::placeholder': {
                  color: colors.outline,
                  opacity: 1,
                },
              }}
            />
          </Box>

          {/* Right side: notifications + avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              sx={{
                color: colors.onSurfaceVariant,
                '&:hover': { color: colors.primary, backgroundColor: 'rgba(0, 62, 199, 0.05)' },
              }}
            >
              <Badge
                variant="dot"
                sx={{
                  '& .MuiBadge-dot': {
                    backgroundColor: colors.error,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    border: `2px solid ${colors.surface}`,
                    top: 3,
                    right: 3,
                  },
                }}
              >
                <NotificationsIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>

            <IconButton onClick={handleAvatarClick} sx={{ p: 0 }}>
              <Avatar
                alt={name}
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: colors.primary,
                  color: colors.onPrimary,
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                {getInitials(name)}
              </Avatar>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* User menu popper */}
      <Popper
        open={Boolean(avatarAnchor)}
        anchorEl={avatarAnchor}
        placement="bottom-end"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setAvatarAnchor(null)}>
          <Box
            sx={{
              backgroundColor: colors.surfaceContainerLowest,
              border: `1px solid ${colors.outlineVariant}`,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              py: 1.5,
              px: 2,
              minWidth: 200,
              mt: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                color: colors.onSurface,
              }}
            >
              {name}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                fontFamily: 'Inter, sans-serif',
                color: colors.outline,
                mt: 0.25,
              }}
            >
              {email}
            </Typography>
            <Divider sx={{ my: 1, borderColor: colors.outlineVariant }} />
            <Box
              component="button"
              onClick={handleLogout}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                py: 0.75,
                px: 0,
                color: colors.error,
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '4px',
                '&:hover': { backgroundColor: 'rgba(186, 26, 26, 0.05)' },
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
              Logout
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>

      {/* Breadcrumbs */}
      <Box
        sx={{
          py: 2,
          px: 4,
        }}
      >
        <Breadcrumbs
          separator={<ChevronRightIcon sx={{ fontSize: 16, color: colors.outlineVariant }} />}
          aria-label="breadcrumb"
          sx={{
            '& .MuiBreadcrumbs-li': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          <Link
            component={RouterLink}
            to="/"
            underline="none"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '14px',
              fontFamily: 'Inter, sans-serif',
              color: colors.outline,
              transition: 'color 0.2s',
              '&:hover': { color: colors.primary },
            }}
          >
            <HomeIcon sx={{ fontSize: 18 }} />
            Home
          </Link>
          {pathnames.map((value, index) => {
            const last = index === pathnames.length - 1;
            const to = `/${pathnames.slice(0, index + 1).join('/')}`;

            return last ? (
              <Typography
                key={to}
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  color: colors.onSurface,
                }}
              >
                {getLabel(value)}
              </Typography>
            ) : (
              <Link
                component={RouterLink}
                to={to}
                underline="none"
                key={to}
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  color: colors.outline,
                  transition: 'color 0.2s',
                  '&:hover': { color: colors.primary },
                }}
              >
                {getLabel(value)}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Box>
    </Box>
  );
}
